import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    ModalDialog, 
    ModalClose, 
    DialogTitle, 
    Button, 
    Stack, 
    Typography, 
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemContent,
    CircularProgress,
    Alert
} from '@mui/joy';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBackends } from '../Internal/api';

export default function BackendSelector({ open, onClose, onBackendSelect }) {
    const dispatch = useDispatch();
    const { availableBackends, currentBackend, loading, error } = useSelector(state => state.backend);
    const [selectedBackend, setSelectedBackend] = useState(currentBackend);

    // Fetch backends when modal opens (only if not already loaded)
    useEffect(() => {
        if (open && availableBackends.length === 0 && !loading) {
            dispatch({ type: 'SET_BACKEND_LOADING', payload: true });
            fetchBackends()
                .then(backends => {
                    dispatch({ type: 'SET_AVAILABLE_BACKENDS', payload: backends });
                })
                .catch(err => {
                    dispatch({ type: 'SET_BACKEND_ERROR', payload: err.message });
                });
        }
    }, [open, availableBackends.length, loading, dispatch]);

    // Update selected backend when current backend changes
    useEffect(() => {
        setSelectedBackend(currentBackend);
    }, [currentBackend]);

    const handleBackendSelect = (backend) => {
        setSelectedBackend(backend);
    };

    const handleConfirm = () => {
        if (selectedBackend) {
            dispatch({ type: 'SET_CURRENT_BACKEND', payload: selectedBackend });
            onBackendSelect(selectedBackend);
        }
        onClose();
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
            <ModalDialog 
                sx={{ 
                    maxWidth: 500, 
                    maxHeight: '70vh', 
                    overflow: 'auto',
                    borderRadius: 'lg',
                    boxShadow: 'lg'
                }}
            >
                <ModalClose onClick={onClose} />
                <DialogTitle sx={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 'lg',
                    color: 'primary.700',
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200',
                    pb: 2,
                    mb: 3
                }}>
                    Select Backend
                </DialogTitle>
                
                <Stack spacing={3}>
                    <Typography level="body-sm" color="neutral">
                        Choose a backend parser for processing your decay trees.
                    </Typography>

                    {loading && (
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                            <CircularProgress size="sm" />
                            <Typography level="body-sm">Loading backends...</Typography>
                        </Stack>
                    )}

                    {error && (
                        <Alert color="danger" variant="soft">
                            <Typography level="body-sm">
                                Error loading backends: {error}
                            </Typography>
                        </Alert>
                    )}

                    {!loading && !error && availableBackends.length > 0 && (
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {availableBackends.map((backend) => (
                                <ListItem key={backend}>
                                    <ListItemButton
                                        selected={selectedBackend === backend}
                                        onClick={() => handleBackendSelect(backend)}
                                        sx={{
                                            borderRadius: 'md',
                                            '&.Mui-selected': {
                                                backgroundColor: 'primary.100',
                                                color: 'primary.700'
                                            }
                                        }}
                                    >
                                        <ListItemContent>
                                            <Typography level="body-md" sx={{ fontWeight: 'md' }}>
                                                {backend}
                                            </Typography>
                                        </ListItemContent>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {!loading && !error && availableBackends.length === 0 && (
                        <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 2 }}>
                            No backends available
                        </Typography>
                    )}
                </Stack>

                <Divider sx={{ marginTop: 3, marginBottom: 3 }} />

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirm}
                        disabled={!selectedBackend}
                        color="primary"
                    >
                        Select Backend
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}
